/**
 * Shared share-watchlist orchestration hook (PRD-6.1, SE-8313).
 *
 * Backs both the desktop ShareWatchlistModal and the mobile
 * MobileShareWatchlistSheet with one owner-only model: an invite row (search a
 * colleague, pick `Can edit` / `Can view`, Invite) above a WHO HAS ACCESS list
 * where each grant can change role or be revoked in place. Done closes; there
 * is no batch submit any more.
 *
 * Since SE-8356 the entity-neutral part lives in {@link useShareGrants}; this
 * hook is the WATCHLIST adapter — the same reads, writes, cache updates and
 * copy as before, now handed to the shared orchestration that the scenario
 * adapter (`useShareScenario`) also uses.
 *
 * Data sources:
 *  - `useWatchlistListUserWatchlistsByWatchlistIdQuery` — the list's grants
 *    (owner + sharees). This is the cache entry every write updates so the
 *    header, toolbar, table and Pitch read the new role without a reload.
 *  - `useWatchlistListMyOrganizationsOrganizationUsersQuery` — invite pool.
 *  - `useAuthGetUserQuery` (in the orchestration) — the signed-in owner,
 *    never offered as an invitee.
 *  All three are skipped while the surface is closed (SE-7783).
 *
 * Writes:
 *  - Invite → `createUserWatchlist` with the chosen role; the returned grant
 *    is appended to the cached grants connection.
 *  - Role change → `updateUserWatchlistRole` with an optimistic `cache.modify`
 *    on the grant's `role`, rolled back on error.
 *  - Revoke → `deleteUserWatchlist`; the grant leaves the connection on success.
 *  Each write then refetches the pills + drawer queries in the background so
 *  the Shared badge and "Shared with N" follow.
 * @module features/scouting/hooks/useShareWatchlist
 */

import { useCallback, useMemo } from "react";

import {
  ShareEntityType,
  WatchlistListUserWatchlistsByWatchlistIdDocument,
  type WatchlistListUserWatchlistsByWatchlistIdQuery,
  useWatchlistCreateUserWatchlistMutation,
  useWatchlistDeleteUserWatchlistMutation,
  useWatchlistListMyOrganizationsOrganizationUsersQuery,
  useWatchlistListUserWatchlistsByWatchlistIdQuery,
  useWatchlistUpdateUserWatchlistRoleMutation,
} from "@/generated/graphql";
import {
  useShareGrants,
  type ShareGrantRow,
  type ShareGrantsAdapter,
  type UseShareGrantsResult,
} from "@/features/scouting/hooks/useShareGrants";
import {
  appendGrant,
  removeGrant,
  shareAccessStateFor,
  type ShareAccessState,
  type ShareCandidateItem,
  type ShareGrantRole,
} from "@/features/scouting/utils/shareWatchlistGrants";
import { getUserDisplayName } from "@/utils/getUserDisplayName";

/** Queries refetched after every grant write: the pills Shared badge and the drawer's "Shared with N". */
const REFETCH_QUERY_NAMES: readonly string[] = [
  "SeeAllListsDrawer",
  "ScoutingListUserWatchlists",
];

/** Page size for the org-users pool — large enough to list a whole org. */
const ORG_USERS_PAGE_SIZE = 200;

/** No one is excluded from a list's invite pool (PRD-6.3: lists carry no tier rule). */
const NO_EXCLUSIONS: readonly ShareCandidateItem[] = [];

/** Arguments for {@link useShareWatchlist}. */
export interface UseShareWatchlistArgs {
  /** Whether the consuming surface (modal/sheet) is open. Gates the queries. */
  readonly isOpen: boolean;
  /** Watchlist id being shared. */
  readonly watchlistId: string;
  /** Watchlist name (toast copy). */
  readonly listName: string;
  /** Dismiss handler — Done and the chrome's close both call it. */
  readonly onClose: () => void;
}

export type { ShareAccessState };

/** Everything a Share surface (AlertDialog or sheet) needs to render and act. */
export type UseShareWatchlistResult = UseShareGrantsResult;

/**
 * Owns the Apollo data, the invite row state and the per-row writes for
 * sharing a watchlist. Breakpoint-agnostic — consumed by both the desktop
 * AlertDialog and the mobile full-screen sheet.
 * @param args - Hook arguments (see {@link UseShareWatchlistArgs})
 * @param args.isOpen - Whether the consuming surface is open
 * @param args.watchlistId - Watchlist id being shared
 * @param args.listName - Watchlist name (toast copy)
 * @param args.onClose - Dismiss handler
 * @returns The render state + handlers for a Share surface.
 */
export const useShareWatchlist = ({
  isOpen,
  watchlistId,
  listName,
  onClose,
}: UseShareWatchlistArgs): UseShareWatchlistResult => {
  const { data: membersData, error: membersError } =
    useWatchlistListUserWatchlistsByWatchlistIdQuery({
      variables: { watchlistId },
      skip: !isOpen,
      fetchPolicy: "cache-and-network",
    });
  const accessState = shareAccessStateFor({
    hasData: Boolean(membersData),
    hasError: Boolean(membersError),
  });

  const { data: orgUsersData } =
    useWatchlistListMyOrganizationsOrganizationUsersQuery({
      variables: { first: ORG_USERS_PAGE_SIZE },
      skip: !isOpen,
      fetchPolicy: "cache-and-network",
    });

  const [createUserWatchlist] = useWatchlistCreateUserWatchlistMutation();
  const [updateUserWatchlistRole] =
    useWatchlistUpdateUserWatchlistRoleMutation();
  const [deleteUserWatchlist] = useWatchlistDeleteUserWatchlistMutation();

  /** Every grant on the list, owner included, with display names. */
  const members = useMemo((): readonly ShareGrantRow[] => {
    const edges = membersData?.listUserWatchlistsByWatchlistId?.edges ?? [];
    return edges.map(edge => ({
      userWatchlistId: edge.node.id,
      userId: edge.node.user.id,
      name: getUserDisplayName(edge.node.user),
      role: edge.node.role,
      // SE-8359: presence rows and INACTIVE members are the orchestration's
      // to hide / mark; a cached read that predates the fields reads DIRECT.
      grantSource: edge.node.grantSource ?? null,
      isMembershipActive: edge.node.isMembershipActive ?? null,
    }));
  }, [membersData]);

  /** Everyone in the organization, including people who already have access. */
  const organizationUsers = useMemo((): readonly ShareCandidateItem[] => {
    const edges =
      orgUsersData?.listMyOrganizationsOrganizationUsers?.edges ?? [];
    return edges
      .map(edge => edge.node.user)
      .filter((user): user is NonNullable<typeof user> => Boolean(user))
      .map(user => ({ userId: user.id, name: getUserDisplayName(user) }));
  }, [orgUsersData]);

  const invite = useCallback(
    async (candidate: ShareCandidateItem, role: ShareGrantRole) => {
      await createUserWatchlist({
        variables: {
          input: { userId: candidate.userId, watchlistId, role },
        },
        update: (cache, { data }) => {
          const grant = data?.createUserWatchlist;
          if (!grant) return;
          cache.updateQuery<WatchlistListUserWatchlistsByWatchlistIdQuery>(
            {
              query: WatchlistListUserWatchlistsByWatchlistIdDocument,
              variables: { watchlistId },
            },
            previous => appendGrant(previous, grant)
          );
        },
      });
    },
    [createUserWatchlist, watchlistId]
  );

  const changeRole = useCallback(
    async (userWatchlistId: string, role: ShareGrantRole) => {
      await updateUserWatchlistRole({
        variables: { input: { userWatchlistId, role } },
      });
    },
    [updateUserWatchlistRole]
  );

  const revoke = useCallback(
    async (userWatchlistId: string) => {
      await deleteUserWatchlist({
        variables: { input: { id: userWatchlistId } },
        update: cache => {
          cache.updateQuery<WatchlistListUserWatchlistsByWatchlistIdQuery>(
            {
              query: WatchlistListUserWatchlistsByWatchlistIdDocument,
              variables: { watchlistId },
            },
            previous => removeGrant(previous, userWatchlistId)
          );
          cache.evict({
            id: cache.identify({
              __typename: "UserWatchlist",
              id: userWatchlistId,
            }),
          });
          cache.gc();
        },
      });
    },
    [deleteUserWatchlist, watchlistId]
  );

  const adapter = useMemo(
    (): ShareGrantsAdapter => ({
      entityId: watchlistId,
      entityType: ShareEntityType.Watchlist,
      entityNoun: "list",
      grantTypename: "UserWatchlist",
      members,
      accessState,
      organizationUsers,
      excludedUsers: NO_EXCLUSIONS,
      excludedCaption: null,
      refetchQueryNames: REFETCH_QUERY_NAMES,
      invite,
      changeRole,
      revoke,
    }),
    [
      watchlistId,
      members,
      accessState,
      organizationUsers,
      invite,
      changeRole,
      revoke,
    ]
  );

  return useShareGrants({ isOpen, entityName: listName, onClose, adapter });
};
